import { useEffect, useRef, useState } from 'react'
import { trackEvent } from 'utils/analytics'
import { couponSteadyVisible } from 'utils/welcomeCouponVisibility'
import { refreshDiscountPreviews } from './useDiscountPreviews.js'

// State and actions behind every welcome coupon surface: the community pages'
// top pill, and the public page's price-card and bottom-bar strips. The public
// page mounts both strips at once (one per breakpoint), so it calls this hook
// once and hands the result to both; the impression then fires once per view.
//
// `placement` names where the impression happened: a string, or a function
// resolved when the impression fires (the public page picks price_card or
// mobile_bar from the viewport width at that moment).
//
// status:
// - 'claimable' → the viewer can act (logged-out: sign up; logged-in: claim)
// - 'claimed'   → claimed in this page view; `coupon` names the expiry date
// - 'held'      → the viewer already holds an unused coupon (`coupon`)
// - null        → nothing to show
export function useWelcomeCouponOffer({ team, currentUser, teamSlug, claimable = false, held = null, onClaim, placement }) {
  // null: not claimed in this view. Object: the coupon just claimed. false:
  // claimed, but the reply held no coupon to show.
  const [claimed, setClaimed] = useState(null)
  const submitting = useRef(false)

  const steadyVisible = couponSteadyVisible({ claimable })

  // Fire the impression once per page view, regardless of later re-renders.
  const impressionFired = useRef(false)
  useEffect(() => {
    if (steadyVisible && !impressionFired.current) {
      impressionFired.current = true
      const where = typeof placement === 'function' ? placement() : placement
      trackEvent('welcome_coupon_banner_impression', { team_slug: teamSlug, placement: where })
    }
  }, [steadyVisible, teamSlug, placement])

  const claim = async (clickedPlacement) => {
    trackEvent('welcome_coupon_cta_click', { team_slug: teamSlug, placement: clickedPlacement })

    if (currentUser) {
      // Logged-in: claim directly. Never touch the intent endpoint or login modal.
      // Guard against rapid double-clicks firing duplicate POSTs (server is safe via
      // the issuance lock, but this avoids wasting the per-user rate limit).
      if (submitting.current) return
      submitting.current = true
      try {
        const res = await fetch(`/api/teams/${team.id}/welcome_coupon_claim`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          // The page's own locale, so the date in the reply matches the page.
          body: JSON.stringify({ locale: document.documentElement.lang })
        })
        if (res.ok) {
          const body = await res.json().catch(() => ({}))
          // No dated coupon in the reply (used up or expired) leaves nothing to say.
          setClaimed(body?.held?.expiresOn ? body.held : false)
          // Package prices on the page now include the coupon.
          refreshDiscountPreviews(team.id)
        }
      } catch (e) {
        // Non-blocking: leave the offer in place so the viewer can retry.
      } finally {
        submitting.current = false
      }
      return
    }

    // Logged-out: record intent, then enter the registration flow.
    try {
      await fetch(`/api/teams/${team.id}/welcome_coupon_intent`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' }
      })
    } catch (e) {
      // Non-blocking: proceed to registration even if intent marking fails.
    }
    onClaim?.()
  }

  let status = null
  let coupon = null
  if (claimed?.expiresOn) {
    status = 'claimed'
    coupon = claimed
  } else if (held?.expiresOn) {
    status = 'held'
    coupon = held
  } else if (steadyVisible && claimed !== false) {
    status = 'claimable'
  }

  return { status, coupon, claim }
}
